import React, { useContext, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from '@iconify/react';
import LoginContext from "../../store/loginContext";
import langContextObj from "../../store/langContext";
import { images } from "../../constants";
import Input from "../UI/input/Input";
import Button from "../UI/button/Button";
import classes from "./Login.module.scss";

function SignupBox() {
  // Context and refs setup
  const loginCtx = useContext(LoginContext);
  const langCtx = useContext(langContextObj);
  const userNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPassRef = useRef<HTMLInputElement>(null);
  const errorMessageRef = useRef<HTMLSpanElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State management
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const API_BASE =
    process.env.REACT_APP_API_URL || "https://api.trafertirgroup.com";
  const API_URL = `${API_BASE}/signup/`;

  // Validation functions
  const validatePassword = (password: string): boolean => {
    return password.length >= 6;
  };

  const validateUsername = (username: string): boolean => {
    return username.length >= 3 && /^[a-zA-Z0-9_]+$/.test(username);
  };

  // Form submission handler
  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage("");

    const username = userNameRef.current?.value;
    const password = passwordRef.current?.value;
    const confirmPassword = confirmPassRef.current?.value;

    if (!username || !password || !confirmPassword) {
      setErrorMessage(t("allFieldsRequired"));
      return;
    }

    if (!validateUsername(username)) {
      setErrorMessage(t("invalidUsernameFormat"));
      return;
    }

    if (!validatePassword(password)) {
      setErrorMessage(t("passwordTooShort"));
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(t("passwordsMustMatch"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userid: Math.random().toString(36).substr(2, 9),
          username,
          password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        flushSync(() => {
          loginCtx.login();
        });
        navigate("/dashboard", { replace: true });
      } else {
        setErrorMessage(data.message || t("signupError"));
        
        if (errorMessageRef.current) {
          errorMessageRef.current.style.display = "inline-block";
          errorMessageRef.current.style.opacity = "1";
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
      setErrorMessage(t("networkError"));
    } finally {
      setIsLoading(false);
    }
  }

  const handleLoginClick = () => navigate("/");
  const handleSignupClick = () => navigate("/signup");

  return (
    <div className={`${classes.container} ${langCtx.lang === "fa" ? classes.rtl : ""}`}>
      <div className={classes.loginBox}>
        <div className={classes.tabRow}>
          <button
            type="button"
            className={classes.tabBtn}
            onClick={handleLoginClick}
            disabled={isLoading}
          >
            Login
          </button>
          <button
            type="button"
            className={`${classes.tabBtn} ${classes.tabBtnActive}`}
            onClick={handleSignupClick}
            disabled={isLoading}
          >
            Sign-up
          </button>
        </div>

        <div className={classes.logo}>
          <img src={images.logo} alt="Trafertir Group" />
        </div>
        <p className={classes.brand}>Trafertir Group</p>
        <h2 className={classes.title}>{t("sign-Up")}</h2>

        <form onSubmit={handleSignup}>
          <Input
            ref={userNameRef}
            type="text"
            id="userName"
            placeholder="Enter username"
            disabled={isLoading}
          />
          <div className={classes.passwordWrap}>
            <Input
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              id="password"
              placeholder="Enter password"
              disabled={isLoading}
            />
            <button
              type="button"
              className={classes.eyeBtn}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon
                icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
                width="20"
                height="20"
              />
            </button>
          </div>
          <div className={classes.passwordWrap}>
            <Input
              ref={confirmPassRef}
              type={showConfirmPassword ? "text" : "password"}
              id="confirm_pass"
              placeholder="Confirm password"
              disabled={isLoading}
            />
            <button
              type="button"
              className={classes.eyeBtn}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              <Icon
                icon={showConfirmPassword ? "mdi:eye-off" : "mdi:eye"}
                width="20"
                height="20"
              />
            </button>
          </div>

          {errorMessage && (
            <span
              ref={errorMessageRef}
              className={`${classes.errorMessage} ${classes.visible}`}
              role="alert"
            >
              {errorMessage}
            </span>
          )}

          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("signingUp") : t("signup")}
          </Button>

          <div className={classes.checkbox}>
            <input
              type="checkbox"
              id="rememberMe"
              disabled={isLoading}
            />
            <label htmlFor="rememberMe">{t("rememberMe")}</label>
          </div>
        </form>
      </div>

      <div className={classes.keyPic}>
        <img
          src={require("../../assets/images/Revenue-cuate.svg").default}
          alt=""
        />
      </div>
    </div>
  );
}

export default SignupBox;
